import Page from "components/Page";
import { withTranslation } from "react-i18next";
import {
  Col,
  Row,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import ToggleSwitch from "components/ToggleSwitch";
import Text from "components/Text";
import constants, { CREDENTIAL_STATUS } from "./../../../../constants";
import { Link } from "react-router-dom";
import { useUserSpecialties } from "repositories/specialty-repository";
import userDefaultImage from "assets/images/staff-default.svg";
import { useUpdateShowInSearchResultMutation } from "repositories/preferences-repository";
import { useCredentialsLicensingGetByUserId } from "repositories/user-repository";
import toast from "react-hot-toast";
import DeleteAccountConfirmationPopup from "./DeleteAccountConfirmationPopup";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import styles from "./profile.module.scss";
import Empty from "components/Empty";
import AddEditLincenseeModal from "./AddEditLincenseeModal";
import ThreeDots from "assets/images/threedots.svg";
import proofMissingWarningIcon from "assets/images/proof-missing-warning.svg";
import moment from "moment";
import DeleteDocumentModal from "./DeleteDocumentModal";
import { CREDENTIAL_TYPE } from "features/credentials/credentialTypes";
import {
  getDocumentStatus,
  hasProofDocument,
} from "../../../CredentialsAndLicensingGlobalListing/CredentialTypeStatus";
import Loader from "components/Loader";
import { decodeId } from "utils";
import { getCredentialDisplayName } from "features/credentials/utils/normalizeCredentialMaster";
import OtherOwnerAwareDropdownItem from "features/credentials/OtherOwnerActionInfo";
const LicenseCardWithHeading = ({
  parentClass = "",
  mainHeading,
  emptyCardEnabled,
  children,
  btnText,
  emptyText,
  emptySubText,
  onAddClick,
  addClickType,
}) => {
  const handleAddClick = useCallback(() => {
    onAddClick(addClickType);
  }, [onAddClick, addClickType]);

  return (
    <div className={styles["licensee-card-parent"] + parentClass}>
      <Text size="16px" weight="600" color="#2D3245">
        {mainHeading}
      </Text>
      <div className={emptyCardEnabled ? styles["licensee-empty-card"] : ""}>
        {emptyCardEnabled ? (
          <Empty
            Message={emptyText}
            anotherChild={
              <Text size="12px" weight="400" color="#6F7788" lineHeight="18px">
                {emptySubText}
              </Text>
            }
          />
        ) : (
          children
        )}
        <button className={styles["add-button"]} onClick={handleAddClick}>
          <img
            src={require("assets/images/plus-icon-outline.svg").default}
            alt="icon"
          />
          <span>{btnText}</span>
        </button>
      </div>
    </div>
  );
};

/** Re-export for existing imports from Profile. */
export { getDocumentStatus };

/**
 * Expiry date label for credential cards / detail views.
 * @param {{ status?: number, expiryDate?: string|null }} item
 * @param {Function} t
 * @param {{ withExpiresPrefix?: boolean, dateFormat?: string }} [options]
 * @returns {string}
 */
export function getCredentialExpiryDisplay(
  item,
  t,
  { withExpiresPrefix = true, dateFormat = "MMM YYYY" } = {},
) {
  const statusId = item?.status != null ? +item.status : null;

  if (statusId === CREDENTIAL_STATUS.NO_EXPIRY) {
    return t("accountOwner.noExpiryStatus");
  }
  if (statusId === CREDENTIAL_STATUS.EXPIRY_UNKNOWN || !item?.expiryDate) {
    return statusId === CREDENTIAL_STATUS.EXPIRY_UNKNOWN
      ? t("accountOwner.expiryUnknownStatus")
      : t("accountOwner.noExpiryDate");
  }

  const formatted = moment(item.expiryDate).format(dateFormat);
  return withExpiresPrefix
    ? `${t("accountOwner.expires")} ${formatted}`
    : formatted;
}
const CertificationCardView = ({
  onView,
  onEdit,
  onDelete,
  certificateName,
  item,
  t,
}) => {
  const { status, customClass } = getDocumentStatus(item, t);
  const isProofMissing = !hasProofDocument(item);
  const isOtherOwner = !!item?.isOtherOwner;
  const canDeleteCredential = item?.isAdmin === true && !isOtherOwner;
  const deleteTooltip =
    item?.isAdmin === true
      ? t("accountOwner.cannotDeleteCredentialAddedByOtherOwner")
      : t("accountOwner.cannotDeleteCredentialContactManager");
  return (
    <div
      className={styles["licensee-card-view"] + " cursor-pointer"}
      onClick={onView}
    >
      <div className="d-flex flex-column">
        <Text
          size="15px"
          weight="600"
          lineHeight="18px"
          marginBottom="2px"
          color="#587E85"
        >
          {certificateName}
        </Text>
        <Text
          size="12px"
          weight="400"
          lineHeight="16px"
          marginBottom="0px"
          color="#2D3245"
        >
          {getCredentialExpiryDisplay(item, t)}
        </Text>
      </div>
      <div className={"actions-status-box"}>
        <span
          className={styles["status"] + " " + styles["status-" + customClass]}
        >
          {status}
        </span>
        {isProofMissing && (
          <>
            <img
              id={`proof-missing-${item.id}`}
              src={proofMissingWarningIcon}
              alt={t("accountOwner.proofMissingStatus")}
              className={styles["proof-missing-icon"]}
              onClick={(e) => e.stopPropagation()}
            />
            <UncontrolledTooltip
              placement="top"
              target={`proof-missing-${item.id}`}
            >
              {t("accountOwner.proofMissingStatus")}
            </UncontrolledTooltip>
          </>
        )}
        <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
          <DropdownToggle caret={false} tag="div" className="cursor-pointer">
            <div className={`ellipsis-icon`}>
              <img src={ThreeDots} alt="ThreeDots" />
            </div>
          </DropdownToggle>
          <DropdownMenu left>
            <DropdownItem onClick={onView}>
              <Text className={"office-dropdown-item"}>
                {t("accountOwner.view")}
              </Text>
            </DropdownItem>
            <OtherOwnerAwareDropdownItem
              label={t("accountOwner.edit")}
              tooltip={t("accountOwner.cannotEditCredentialAddedByOtherOwner")}
              tooltipId={`profile-other-owner-edit-${item.id}`}
              isOtherOwner={isOtherOwner}
              onClick={onEdit}
            />
            <OtherOwnerAwareDropdownItem
              label={t("accountOwner.delete")}
              tooltip={deleteTooltip}
              tooltipId={`profile-delete-${item.id}`}
              disabled={!canDeleteCredential}
              onClick={onDelete}
            />
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    </div>
  );
};
export const CredentialDataDescription = ({ heading, description }) => {
  return (
    <div className={styles["credential-card__row"]}>
      <dt className={styles["credential-card__label"]}>{heading}</dt>
      <dd className={styles["credential-card__value"]}>{description}</dd>
    </div>
  );
};
function Profile({ history, t, location }) {
  const {
    isOwner: isAccountOwner,
    isStaff,
    profile,
    selectedId,
  } = useAccountOwnerCommon();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);
  const [activeTab, setActiveTab] = useState("1");
  const [viewCertificate, setViewCertificate] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [
    isDeleteAccountConfirmationPopupOpen,
    setIsDeleteAccountConfirmationPopupOpen,
  ] = useState(false);
  const selectedItemRef = useRef(null);
  const [credentialLicenseId, setCredentialLicenseId] = useState(null);
  useEffect(() => {
    const searchString = location.search;
    if (searchString) {
      // Use the native URLSearchParams interface to parse the string
      const queryParams = new URLSearchParams(searchString);
      // Access specific parameters
      const query = queryParams.get("tab");
      if (query) {
        setActiveTab(query);
      }
      const credentialLicenseId = queryParams.get("credId");
      if (credentialLicenseId) {
        setCredentialLicenseId(+decodeId(credentialLicenseId));
      }
    }
  }, [location]);

  const {
    isLoading,
    error,
    data: specialties,
  } = useUserSpecialties(profile.id);
  const {
    data: licenseAllData,
    refetch: refetchLicenseData,
    isLoading: isLoadingLicenseData,
  } = useCredentialsLicensingGetByUserId(profile.id, selectedId, {
    enabled: activeTab === "3" && !!profile.id && !!selectedId,
  });
  const handleView = useCallback((item) => {
    if (selectedItemRef.current && item.id === selectedItemRef.current.id) {
      return;
    }
    selectedItemRef.current = item;
    setViewCertificate(true);
  }, []);

  const handleEdit = useCallback((item, type) => {
    if (item?.isOtherOwner) return;
    selectedItemRef.current = item;
    setIsModalOpen(type);
  }, []);

  const handleDelete = useCallback((item) => {
    if (item?.isOtherOwner || item?.isAdmin !== true) return;
    selectedItemRef.current = item;
    setIsDeleteModalOpen(true);
  }, []);

  const { licenseData, certificateData, trainingData } = useMemo(() => {
    if (!licenseAllData) {
      return { licenseData: [], certificateData: [], trainingData: [] };
    }

    return licenseAllData.reduce(
      (acc, item) => {
        if (credentialLicenseId && item.id === credentialLicenseId) {
          handleView(item);
        }
        if (item.credentialType === 1) {
          acc.licenseData.push(item);
        } else if (item.credentialType === 2) {
          acc.certificateData.push(item);
        } else if (item.credentialType === 3) {
          acc.trainingData.push(item);
        }
        return acc;
      },
      { licenseData: [], certificateData: [], trainingData: [] },
    );
  }, [licenseAllData, credentialLicenseId, handleView]);

  const renderCertificationCards = useCallback(
    (items, type) => {
      return items.map((item) => (
        <CertificationCardView
          key={item.id}
          certificateName={getCredentialDisplayName(item)}
          item={item}
          onView={() => handleView(item)}
          onEdit={() => handleEdit(item, type)}
          onDelete={() => handleDelete(item)}
          t={t}
        />
      ));
    },
    [handleView, handleEdit, handleDelete, t],
  );

  const credentialSections = useMemo(
    () => [
      {
        mainHeading: t("accountOwner.primaryLicense"),
        emptyText: t("accountOwner.noLicensesAddedYet"),
        emptySubText: t("accountOwner.addLicenseDescription"),
        btnText:
          licenseData.length > 0
            ? t("accountOwner.addAnotherLicense")
            : t("accountOwner.addLicense"),
        data: licenseData,
        addClickType: "add_license",
        editClickType: "edit_license",
        parentClass: "",
      },
      {
        mainHeading: t("accountOwner.certifications"),
        emptyText: t("accountOwner.noCertificationsAvailable"),
        emptySubText: t("accountOwner.addCertificationDescription"),
        btnText:
          certificateData.length > 0
            ? t("accountOwner.addAnotherCertification")
            : t("accountOwner.addCertification"),
        data: certificateData,
        addClickType: "add_certificate",
        editClickType: "edit_certificate",
        parentClass: " " + styles["margin-top-40"],
      },
      {
        mainHeading: t("accountOwner.training"),
        emptyText: t("accountOwner.noTrainingRecordsFound"),
        emptySubText: t("accountOwner.addTrainingDescription"),
        btnText:
          trainingData.length > 0
            ? t("accountOwner.addAnotherTraining")
            : t("accountOwner.addTraining"),
        data: trainingData,
        addClickType: "add_training",
        editClickType: "edit_training",
        parentClass: " " + styles["margin-top-40"],
      },
    ],
    [licenseData, certificateData, trainingData, t],
  );

  let isFreePackageSubscription = false;
  let isFreeOrTrialPackageSubscription = false;
  if (profile && profile.userSubscription) {
    isFreePackageSubscription =
      profile.userSubscription.packageType === constants.packageTypes.free;
    isFreeOrTrialPackageSubscription =
      isFreePackageSubscription ||
      profile.userSubscription.packageType === constants.packageTypes.trial;
  }

  let isDoctor = false;

  if (profile && profile.isDoctor) {
    isDoctor = profile.isDoctor;
  }
  const goToPreviousScreen = () => {
    history.push(
      isAccountOwner
        ? constants.routes.accountOwner.offices
        : constants.routes.staff.offices,
    );
  };

  const addDefaultSrc = (ev) => {
    ev.target.src = userDefaultImage;
    ev.target.onerror = null;
  };

  const viewCertificateData = useMemo(() => {
    return selectedItemRef.current;
  }, [viewCertificate, isModalOpen, isDeleteModalOpen]);
  const viewCertificateStatus = getDocumentStatus(viewCertificateData, t);

  const handleDownloadDocument = async (fileLink) => {
    if (!fileLink) return;
    try {
      toast.success(t("accountOwner.downloadingDocument"));
      const response = await fetch(fileLink);
      const blob = await response.blob();

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileLink.split("/").pop();

      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(t("accountOwner.documentDownloadedSuccessfully"));
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error(t("accountOwner.errorDownloadingFile"));
    }
  };
  return (
    <>
      {isDeleteAccountConfirmationPopupOpen && (
        <DeleteAccountConfirmationPopup
          onClose={() => setIsDeleteAccountConfirmationPopupOpen(false)}
        />
      )}
      {isLoadingLicenseData && <Loader />}
      <Page title={t("accountOwner.profile")} onBack={goToPreviousScreen}>
        <div className="profile-tab-wrapper">
          <div className="container px-0">
            <div className="form-wrapper">
              <Row className="no-gutters">
                {/* Section 1 */}
                <Col
                  lg={
                    isFreeOrTrialPackageSubscription ||
                    isAccountOwner ||
                    isDoctor
                      ? "8"
                      : "12"
                  }
                >
                  {/* Profile heading */}
                  <div
                    className={
                      "profile-detail-col" +
                      (isFreeOrTrialPackageSubscription ||
                      isDoctor ||
                      isAccountOwner
                        ? " divider"
                        : "")
                    }
                  >
                    <div className="image-warepper">
                      <div className={`${styles["profile-wrapper-box"]}`}>
                        <div className="profile-img">
                          <img
                            src={profile.profilePic || userDefaultImage}
                            onError={addDefaultSrc}
                            alt="profile"
                          />
                        </div>
                        <div className="profile-name">
                          {`${profile.firstName} ${profile.lastName}`}
                        </div>
                      </div>
                      <div className="profile-dropdown">
                        <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                          <DropdownToggle
                            caret={false}
                            tag="div"
                            className="dropdown-btn"
                          >
                            <img
                              src={
                                require("assets/images/dots-icon.svg").default
                              }
                              alt="icon"
                            />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem>
                              <Link to={constants.routes.editProfile}>
                                {t("accountOwner.editProfile")}
                              </Link>
                            </DropdownItem>
                            <DropdownItem>
                              <Link to={constants.routes.changePassword}>
                                <span className="menu-hover-effect">
                                  {t("accountOwner.changePasswordLink")}
                                </span>
                              </Link>
                            </DropdownItem>
                            {isFreePackageSubscription && isAccountOwner && (
                              <DropdownItem
                                onClick={() =>
                                  setIsDeleteAccountConfirmationPopupOpen(true)
                                }
                              >
                                <span>{t("accountOwner.deleteMyAccount")}</span>
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </div>
                    <div className={"common-tabs " + styles["nav-tabs"]}>
                      <Nav tabs>
                        <NavItem>
                          <NavLink
                            className={activeTab === "1" ? "active" : ""}
                            onClick={() => setActiveTab("1")}
                          >
                            {t("accountOwner.basicInfo")}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={activeTab === "2" ? "active" : ""}
                            onClick={() => setActiveTab("2")}
                          >
                            {t("accountOwner.medicalBackground")}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={activeTab === "3" ? "active" : ""}
                            onClick={() => setActiveTab("3")}
                          >
                            {t("accountOwner.credentialsAndLicensing")}
                          </NavLink>
                        </NavItem>
                      </Nav>
                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                          <div className="profile-basic-content">
                            {(isAccountOwner || isStaff) && (
                              <>
                                <DetailItem
                                  title={
                                    t("form.fields.fullName") +
                                    "/" +
                                    t("form.fields.companyName")
                                  }
                                  value={
                                    profile.firstName + " " + profile.lastName
                                  }
                                />

                                <DetailItem
                                  title={t("form.fields.contactNumber")}
                                  value={profile.contactNumber}
                                />
                                {isStaff && (
                                  <DetailItem
                                    title={t(
                                      "form.fields.socialSecurityNumber",
                                    )}
                                    value={profile.socialSecurityNumber || "--"}
                                  />
                                )}

                                <DetailItem
                                  title={t(
                                    isStaff
                                      ? "form.fields.street"
                                      : "accountOwner.practiceAddress",
                                  )}
                                  value={
                                    isStaff
                                      ? profile?.streetAddress
                                      : profile.address
                                  }
                                />

                                <DetailItem
                                  title={t("form.fields.country")}
                                  value={profile.country}
                                />

                                <DetailItem
                                  title={t("form.fields.provinceOrState")}
                                  value={
                                    isStaff ? profile.state : profile.province
                                  }
                                />

                                <DetailItem
                                  title={t("form.fields.city")}
                                  value={profile.city}
                                />

                                <DetailItem
                                  title={t("form.fields.postalCode")}
                                  value={profile.postalCode}
                                />
                              </>
                            )}

                            {!isAccountOwner && !isStaff && (
                              <>
                                <DetailItem
                                  title={t("form.fields.fullName")}
                                  value={
                                    profile.firstName + " " + profile.lastName
                                  }
                                />

                                <DetailItem
                                  title={t("role")}
                                  value={profile.designation || "-"}
                                />

                                <DetailItem
                                  title={t("form.fields.emailAddress")}
                                  value={profile.emailId}
                                />

                                <DetailItem
                                  title={t("form.fields.contactNumber")}
                                  value={profile.contactNumber}
                                />
                              </>
                            )}
                          </div>
                        </TabPane>
                        <TabPane tabId="2">
                          <div className="medical-content">
                            <DetailItem
                              title={t("form.fields.yearsOfExperience")}
                              value={
                                profile.yearsOfExperience
                                  ? `${profile.yearsOfExperience} ${
                                      profile.yearsOfExperience === 1
                                        ? t("accountOwner.year").toLowerCase()
                                        : t("accountOwner.years")
                                    }`
                                  : t("accountOwner.notAdded")
                              }
                            />

                            <Specialties
                              specialties={
                                !isLoading && !error ? specialties : []
                              }
                              t={t}
                            />

                            <DetailItem
                              title={t("form.fields.licenseId")}
                              value={
                                profile.licenseId
                                  ? profile.licenseId
                                  : t("accountOwner.notAdded")
                              }
                            />
                          </div>
                        </TabPane>
                        <TabPane tabId="3">
                          {viewCertificate ? (
                            <div className="d-flex flex-column">
                              <button
                                className="back-btn"
                                style={{
                                  marginBottom: "20px",
                                  textAlign: "start",
                                }}
                                onClick={() => {
                                  setViewCertificate(false);
                                  selectedItemRef.current = null;
                                }}
                              >
                                <div className="back-btn-text">
                                  <span className="ico">
                                    <img
                                      src={
                                        require("assets/images/arrow-back-icon.svg")
                                          .default
                                      }
                                      alt="arrow"
                                    />
                                  </span>
                                  {t("back")}
                                </div>
                              </button>
                              <Text
                                size="16px"
                                lineHeight="18px"
                                weight="600"
                                color="#2D3245"
                                marginBottom="20px"
                              >
                                {getCredentialDisplayName(viewCertificateData)}
                              </Text>
                              <div
                                className="d-flex flex-column flex-wrap green-main-color-form"
                                style={{ padding: "20px" }}
                              >
                                <div className="d-flex justify-content-between w-100">
                                  <CredentialDataDescription
                                    heading={t("accountOwner.credentialName")}
                                    description={getCredentialDisplayName(
                                      viewCertificateData,
                                    )}
                                  />
                                  {viewCertificateStatus.showHide && (
                                    <span
                                      className={
                                        styles["status"] +
                                        " " +
                                        styles[
                                          "status-" +
                                            viewCertificateStatus.customClass
                                        ]
                                      }
                                    >
                                      {viewCertificateStatus.status}
                                    </span>
                                  )}
                                </div>
                                <CredentialDataDescription
                                  heading={t("accountOwner.type")}
                                  description={
                                    CREDENTIAL_TYPE.find(
                                      (type) =>
                                        type.id === viewCertificateData.type,
                                    )?.name || "--"
                                  }
                                />
                                <CredentialDataDescription
                                  heading={t("accountOwner.issuingAuthority")}
                                  description={
                                    viewCertificateData.issuingAuthority || "--"
                                  }
                                />
                                <CredentialDataDescription
                                  heading={t("accountOwner.credentialId")}
                                  description={
                                    viewCertificateData.credentialId || "--"
                                  }
                                />
                                <CredentialDataDescription
                                  heading={t("accountOwner.issueDate")}
                                  description={
                                    viewCertificateData.issueDate
                                      ? moment(
                                          viewCertificateData.issueDate,
                                        ).format("DD-MM-YYYY")
                                      : "--"
                                  }
                                />
                                <CredentialDataDescription
                                  heading={t("accountOwner.expiryDate")}
                                  description={getCredentialExpiryDisplay(
                                    viewCertificateData,
                                    t,
                                    {
                                      withExpiresPrefix: false,
                                      dateFormat: "DD-MM-YYYY",
                                    },
                                  )}
                                />
                                <div className={styles["credential-card__row"]}>
                                  <dt
                                    className={styles["credential-card__label"]}
                                  >
                                    {t("accountOwner.proofDocument")}
                                  </dt>
                                  <dd
                                    className={
                                      styles["credential-card__value"] +
                                      (viewCertificateData.proofDocumentPath?.trim()
                                        ? " d-flex justify-content-between align-items-center"
                                        : "")
                                    }
                                  >
                                    {viewCertificateData.proofDocumentPath?.trim() ? (
                                      <>
                                        <span>
                                          {viewCertificateData.proofDocumentPath
                                            .split("/")
                                            .pop()}
                                        </span>
                                        <button
                                          className={styles["downloading-btn"]}
                                          id="download-document"
                                          onClick={() =>
                                            handleDownloadDocument(
                                              viewCertificateData.proofDocumentPath,
                                            )
                                          }
                                        >
                                          {t("accountOwner.download")}
                                        </button>
                                      </>
                                    ) : (
                                      "--"
                                    )}
                                  </dd>
                                </div>
                                <div className={styles["credential-card__row"]}>
                                  <dl>
                                    <dt
                                      className={
                                        styles["credential-card__label"]
                                      }
                                    >
                                      {t("accountOwner.additionalDocuments")}
                                    </dt>
                                    {viewCertificateData.additionalDocuments
                                      ?.length > 0
                                      ? viewCertificateData.additionalDocuments.map(
                                          (document, i) => (
                                            <dd
                                              key={document.id}
                                              className={
                                                styles[
                                                  "credential-card__value"
                                                ] +
                                                " d-flex justify-content-between align-items-center"
                                              }
                                            >
                                              <span>
                                                {document.documentPath
                                                  .split("/")
                                                  .pop()}
                                              </span>
                                              <button
                                                className={
                                                  styles["downloading-btn"]
                                                }
                                                id={"download-document" + i}
                                                onClick={() =>
                                                  handleDownloadDocument(
                                                    document.documentPath,
                                                  )
                                                }
                                              >
                                                {t("accountOwner.download")}
                                              </button>
                                            </dd>
                                          ),
                                        )
                                      : "--"}
                                  </dl>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              {credentialSections.map((section) => (
                                <LicenseCardWithHeading
                                  key={section.addClickType}
                                  mainHeading={section.mainHeading}
                                  emptyText={section.emptyText}
                                  emptySubText={section.emptySubText}
                                  btnText={section.btnText}
                                  emptyCardEnabled={section.data.length === 0}
                                  addClickType={section.addClickType}
                                  onAddClick={(type) => setIsModalOpen(type)}
                                  parentClass={section.parentClass}
                                >
                                  {renderCertificationCards(
                                    section.data,
                                    section.editClickType,
                                  )}
                                </LicenseCardWithHeading>
                              ))}
                            </>
                          )}
                          {isModalOpen && (
                            <AddEditLincenseeModal
                              isModalOpen={isModalOpen !== null}
                              closeModal={() => {
                                setIsModalOpen(null);
                                selectedItemRef.current = null;
                              }}
                              heading={isModalOpen.replace("_", " ")}
                              editData={viewCertificateData}
                              refetchData={() => refetchLicenseData()}
                              isFromProfile
                            />
                          )}
                          {isDeleteModalOpen && (
                            <DeleteDocumentModal
                              credentialId={selectedItemRef.current?.id}
                              onClose={() => {
                                setIsDeleteModalOpen(false);
                                selectedItemRef.current = null;
                              }}
                              refetchData={refetchLicenseData}
                            />
                          )}
                          {/* Section 2 */}
                        </TabPane>
                      </TabContent>
                    </div>
                  </div>
                </Col>
                {(isFreeOrTrialPackageSubscription ||
                  isAccountOwner ||
                  isDoctor) && (
                  <Col lg="4">
                    <div className="download-app-col">
                      {(isFreePackageSubscription ||
                        isAccountOwner ||
                        isDoctor) && (
                        <DoctorSearchResultToggle
                          shouldShow={profile.showInSearch}
                          t={t}
                        />
                      )}
                      {isFreeOrTrialPackageSubscription && (
                        <div className="download-wrapper">
                          <Text
                            secondary
                            size="16px"
                            weight="600"
                            marginBottom="15px"
                          >
                            {t("subscription")}
                          </Text>
                          <Text color="#6f7788" size="12px" marginBottom="30px">
                            {t("accountOwner.upgradeSubscriptionDescription")}
                          </Text>

                          <Link
                            className="button button-round button-shadow"
                            to={constants.routes.accountOwner.managePlan}
                          >
                            {t("accountOwner.upgradeSubscription")}
                          </Link>
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function DetailItem({ title, value }) {
  return (
    <div className="c-field">
      <label>{title}</label>
      <div className="field-name">{value}</div>
    </div>
  );
}

function Specialties({ specialties, t }) {
  let content = null;

  if (specialties.length > 0) {
    content = specialties.map((it, i) => (
      <div className="field-name" key={i}>
        {it.title}
      </div>
    ));
  } else {
    content = <div className="field-name">{t("notAdded")}</div>;
  }

  return (
    <div className="c-field">
      <label>{t("form.fields.specialtiesAndServices")}</label>
      {content}
    </div>
  );
}

function DoctorSearchResultToggle({ shouldShow, t }) {
  const mutation = useUpdateShowInSearchResultMutation();

  const onToggle = async () => {
    try {
      await mutation.mutateAsync(!shouldShow);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="search-database-box">
      <Text color="#111b45" size="13px">
        {t("accountOwner.showInSearchDatabase")}
      </Text>
      <ToggleSwitch
        label="searchDatabase"
        value={shouldShow}
        onChange={onToggle}
      />
    </div>
  );
}

export default withTranslation()(Profile);
